import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { ReactComponent as AlarmWarningIcon } from 'src/assets/svg/ri-alarm-warning-fill.svg'
import { ReactComponent as FlashlightIcon } from 'src/assets/svg/ri-flashlight-fill.svg'
import { ReactComponent as LightbulbIcon } from 'src/assets/svg/ri_lightbulb-line.svg'
import { Skeleton } from 'src/components/Skeleton'
import { Tooltip } from 'src/components/Tooltip'
import { BaseModal } from 'src/containers/BaseModal'
import { useGetInboxMetaInsightsQuery } from 'src/reactQuery/useGetInboxMetaInsightsQuery'
import { MetaCard } from 'src/services/__generated__/api'
import { parseHighlights } from 'src/utils/parseHighlights'
import classes from './metaInsights.module.scss'

type CardKind = MetaCard['kind']

type FilterKey = 'all' | CardKind

const TABS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'trend', label: 'Trends' },
  { key: 'opportunity', label: 'Opportunities' },
  { key: 'risk', label: 'Risks' },
]

const KIND_LABEL: Record<CardKind, string> = {
  opportunity: 'Opportunity',
  risk: 'Risk',
  trend: 'Trend',
}

// ============================================================
// TODO: NEEDS BACKEND — the per-card "impact" pill (High Impact /
// High Priority / Medium) is hardcoded by kind. The current
// MetaCard schema doesn't carry a severity/impact field; once
// backend exposes one, drive this off real data.
// ============================================================

const IMPACT_LABEL: Record<
  CardKind,
  { label: string; tone: 'high' | 'medium' }
> = {
  opportunity: { label: 'Medium', tone: 'medium' },
  risk: { label: 'High Priority', tone: 'high' },
  trend: { label: 'High Impact', tone: 'high' },
}

const KIND_ICON: Record<CardKind, FC<React.SVGProps<SVGSVGElement>>> = {
  opportunity: LightbulbIcon,
  risk: AlarmWarningIcon,
  trend: FlashlightIcon,
}

interface MetaInsightsProps {
  fromDate?: string
  pageId: string
  toDate?: string
}

const HighlightedText: FC<{ value: string }> = ({ value }) => {
  const segments = useMemo(() => parseHighlights(value), [value])
  return (
    <>
      {segments.map((seg, i) => (
        <Fragment key={i}>
          {seg.color ? (
            <span className={`${classes.highlight} ${classes[seg.color]}`}>
              {seg.text}
            </span>
          ) : (
            seg.text
          )}
        </Fragment>
      ))}
    </>
  )
}

// Renders highlight-tagged text clamped to `maxLines`. When the text actually
// overflows (the ellipsis is showing), the full text is revealed on hover via
// the shared Tooltip — highlights preserved in both the body and the tooltip.
const ClampedText: FC<{
  className?: string
  maxLines: number
  value: string
}> = ({ className, maxLines, value }) => {
  const ref = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return undefined

    const update = () => {
      if (
        !node.isConnected ||
        (node.clientHeight === 0 && node.clientWidth === 0)
      ) {
        return
      }
      setIsTruncated(node.scrollHeight - node.clientHeight > 1)
    }

    update()
    const rafId = requestAnimationFrame(update)
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined
    fonts?.ready?.then(update).catch(() => undefined)
    fonts?.addEventListener?.('loadingdone', update)
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    resizeObserver?.observe(node)
    window.addEventListener('resize', update)

    return () => {
      cancelAnimationFrame(rafId)
      fonts?.removeEventListener?.('loadingdone', update)
      resizeObserver?.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [maxLines, value])

  // The clamp keeps every card about the same height: the row is as tall as
  // its tallest card, so one long insight left white space under the others.
  const body = (
    <span
      className={className}
      ref={ref}
      style={{
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: maxLines,
        display: '-webkit-box',
        overflow: 'hidden',
      }}
    >
      <HighlightedText value={value} />
    </span>
  )

  if (!isTruncated) return body

  return (
    <Tooltip
      mouseEnterDelay={0.1}
      overlay={<HighlightedText value={value} />}
      placement="top"
    >
      {body}
    </Tooltip>
  )
}

const InsightCard: FC<{ card: MetaCard; onClick: () => void }> = ({
  card,
  onClick,
}) => {
  const Icon = KIND_ICON[card.kind]
  const impact = IMPACT_LABEL[card.kind]

  return (
    <div className={`${classes.card} ${classes[card.kind]}`} onClick={onClick}>
      <div className={classes.kindRow}>
        <div className={`${classes.kindIcon} ${classes[card.kind]}`}>
          <Icon />
        </div>
        <div className={classes.kindLabelGroup}>
          <span className={`${classes.kindLabel} ${classes[card.kind]}`}>
            {KIND_LABEL[card.kind]}
          </span>
          <span className={classes.kindBullet}>•</span>
          <span className={`${classes.impact} ${classes[impact.tone]}`}>
            {impact.label}
          </span>
        </div>
      </div>

      <div className={classes.cardTitleWrap}>
        <div className={`${classes.cardTitleBar} ${classes[card.kind]}`} />
        <ClampedText
          className={classes.cardTitle}
          maxLines={3}
          value={card.title}
        />
      </div>

      <ClampedText
        className={classes.cardDetail}
        maxLines={4}
        value={card.detail}
      />

      <div className={classes.recommendation}>
        <div className={`${classes.recommendationLabel} ${classes[card.kind]}`}>
          <LightbulbIcon />
          Recommendation
        </div>
        <ClampedText
          className={classes.cardRecommendation}
          maxLines={4}
          value={card.recommendation}
        />
      </div>
    </div>
  )
}

// Mirrors InsightCard inside a real .card, so the row keeps its final height
// and the three-across layout while the insights load. Dimensions from
// metaInsights.module.scss: .kindIcon is 36x36 radius 10, .kindLabelGroup is
// 14px text, .cardTitle/.cardRecommendation are 16px on a 20px line-height,
// and .cardDetail is 13px on 18px.
const InsightCardSkeleton: FC = () => (
  <div className={classes.card}>
    <div className={classes.kindRow}>
      <Skeleton height={36} radius={10} width={36} />
      <div className={classes.kindLabelGroup}>
        <Skeleton height={14} width={72} />
        <Skeleton height={13} width={54} />
      </div>
    </div>

    <div className={classes.cardTitleWrap}>
      <Skeleton height={20} width="88%" />
    </div>

    <div className={classes.skeletonLines}>
      <Skeleton height={18} width="100%" />
      <Skeleton height={18} width="64%" />
    </div>

    <div className={classes.recommendation}>
      <Skeleton height={12} width={112} />
      <Skeleton height={20} width="76%" />
    </div>
  </div>
)

const DetailModal: FC<{ card: MetaCard | null; onClose: () => void }> = ({
  card,
  onClose,
}) => {
  if (!card) return null
  return (
    <BaseModal isOpen={!!card} onRequestClose={onClose}>
      <div className={classes.modal}>
        <div className={classes.modalHeader}>
          <h2 className={classes.modalTitle}>
            <HighlightedText value={card.title} />
          </h2>
          <button
            className={classes.modalCloseButton}
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </div>

        <div className={classes.modalSection}>
          <div className={classes.modalSectionLabel}>Detail</div>
          <p className={classes.modalText}>
            <HighlightedText value={card.detail} />
          </p>
        </div>

        <div className={classes.modalSection}>
          <div className={classes.modalSectionLabel}>Recommendation</div>
          <p className={classes.modalText}>
            <HighlightedText value={card.recommendation} />
          </p>
        </div>

        <div className={classes.modalMeta}>
          <span>{KIND_LABEL[card.kind]}</span>
          {card.sourceInsightIds?.length ? (
            <span>
              Based on {card.sourceInsightIds.length} insight
              {card.sourceInsightIds.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>
      </div>
    </BaseModal>
  )
}

export const MetaInsights: FC<MetaInsightsProps> = ({
  fromDate,
  pageId,
  toDate,
}) => {
  const [activeTab, setActiveTab] = useState<FilterKey>('all')
  const [selectedCard, setSelectedCard] = useState<MetaCard | null>(null)

  const { data, isLoading } = useGetInboxMetaInsightsQuery({
    fromDate,
    pageId,
    toDate,
  })

  const cards = data?.cards ?? []

  const filteredCards = useMemo(() => {
    if (activeTab === 'all') return cards
    return cards.filter((c) => c.kind === activeTab)
  }, [activeTab, cards])

  const isEmpty = !isLoading && (!data?.hasInsights || cards.length === 0)

  // ===== Drag-to-scroll =====
  // Native overflow-x: auto handles touch swipe; mouse drag needs
  // explicit pointer handlers. We track movement and flip an
  // `isDragging` flag once the pointer crosses a small threshold,
  // which both styles the cursor and suppresses the click-through
  // to the card (so dragging never opens the modal).
  const scrollerRef = useRef<HTMLDivElement>(null)
  const dragState = useRef({
    isDown: false,
    moved: false,
    scrollLeft: 0,
    startX: 0,
  })
  const [isDragging, setIsDragging] = useState(false)

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!scrollerRef.current) return
    dragState.current = {
      isDown: true,
      moved: false,
      scrollLeft: scrollerRef.current.scrollLeft,
      startX: e.clientX,
    }
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragState.current.isDown || !scrollerRef.current) return
    const dx = e.clientX - dragState.current.startX
    if (Math.abs(dx) > 5) {
      if (!dragState.current.moved) {
        dragState.current.moved = true
        setIsDragging(true)
      }
      scrollerRef.current.scrollLeft = dragState.current.scrollLeft - dx
    }
  }

  const endDrag = () => {
    dragState.current.isDown = false
    // Defer clearing so the synthetic click that follows pointerup
    // can still see the dragging flag and bail out.
    setTimeout(() => setIsDragging(false), 0)
  }

  const handleCardClick = (card: MetaCard) => {
    if (dragState.current.moved) return
    setSelectedCard(card)
  }

  return (
    <div className={classes.metaInsights}>
      <div className={classes.header}>
        <h2 className={classes.title}>Insights Summary</h2>
        <div className={classes.tabs}>
          {TABS.map((tab) => (
            <button
              className={`${classes.tab} ${
                activeTab === tab.key ? classes.active : ''
              }`}
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Three skeleton cards — the number the .cards row shows at once — so
          the widget holds its real height instead of collapsing to a single
          line of text and jumping open when the insights arrive. */}
      {isLoading && (
        <div className={classes.cards}>
          {Array.from({ length: 3 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <InsightCardSkeleton key={index} />
          ))}
        </div>
      )}

      {!isLoading && isEmpty && (
        <div className={classes.empty}>No insights summary available yet.</div>
      )}

      {!isLoading && !isEmpty && (
        <div
          className={`${classes.cards} ${isDragging ? classes.dragging : ''}`}
          onPointerCancel={endDrag}
          onPointerDown={onPointerDown}
          onPointerLeave={endDrag}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          ref={scrollerRef}
        >
          {filteredCards.map((card) => (
            <InsightCard
              card={card}
              key={card.id}
              onClick={() => handleCardClick(card)}
            />
          ))}
        </div>
      )}

      <DetailModal card={selectedCard} onClose={() => setSelectedCard(null)} />
    </div>
  )
}
